import React, { useState, useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useBoolean } from '@rothzerg/useboolean'
import { isEmpty } from '@rothzerg/validator'
import moment from 'moment'
import axios from 'axios'

import FormInput from '../ui/Form/FormInput'
import ButtonCta from './atoms/ButtonCta'
import Gtm from '../../utils/gtm'
import { loggingEnabled } from '../../utils/logging'

const useStyles = createUseStyles(theme => ({
  container: {
    margin: '0 auto',
    width: '100%',
    maxWidth: '1220px',
    paddingBottom: 50,
    '@media (max-width: 600px)': {
      boxSizing: 'border-box',
      padding: '0 15px 40px 15px',
    },
  },
  popupTitle: {
    fontFamily: 'TTRounds',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '15px',
    lineHeight: '17px',
  },
  popupItem: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid #F0F0F0',
    padding: '8px 0',
    '@media (max-width: 700px)': {
      display: 'block',
      '& div:nth-child(2)': {
        paddingTop: 5,
      },
    },
  },
  form: {
    maxWidth: 400,
  },
  desc: {
    fontSize: 16,
    lineHeight: 1.5,
    letterSpacing: '0.02em',
    color: '#000',
    marginBottom: '50px',
    fontWeight: 'normal',
  },
  flexCont: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
      gap: 0,
    },
  },
  buttonPrimary: {
    display: 'inline-block',
    padding: '18px 30px 16px',
    color: '#fff',
    backgroundColor: theme.colors.orange,
    border: `2px solid ${theme.colors.orange}`,
    borderRadius: '18px',
    lineHeight: '18px',
    fontSize: '18px',
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'all 250ms cubic-bezier(.215,.61,.355,1)',
    '&:hover': {
      backgroundColor: theme.colors.orangeHover,
      border: `2px solid ${theme.colors.orangeHover}`,
      cursor: 'pointer',
    },
    '&:disabled, &[disabled]': {
      backgroundColor: '#cccccc',
      borderColor: '#cccccc',
      color: '#666666',
    },
  },
  cancelButton: {
    display: 'block',
    border: 'none',
    width: '100%',
    boxSizing: 'border-box',
    padding: '20px 0',
    backgroundColor: '#EE2049',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#fff',
    textTransform: 'uppercase',
    cursor: 'pointer',
    '&:disabled, &[disabled]': {
      backgroundColor: '#cccccc',
      borderColor: '#cccccc',
      color: '#666666',
    },
  },
  buttons: {
    minHeight: 300,
    textAlign: 'left',
    '@media (max-width: 600px)': {
      '& a': {
        display: 'block',
      },
    },
    '& a + a': {
      marginLeft: '20px',
      '@media (max-width: 600px)': {
        marginLeft: '0',
        marginTop: '20px',
      },
    },
  },
}))

const CancelBookingForm = ({ bookingCancelled }) => {
  const classes = useStyles()
  const gtm = new Gtm(loggingEnabled)
  const [cancelled, setCancelled] = useBoolean(bookingCancelled == '1')
  const [formSubmitting, setFormSubmitting] = useBoolean()
  const [formStatus, setFormStatus] = useState(null)
  const [bookingDetails, setBookingDetails] = useState(null)
  const [bookingNumber, setBookingNumber] = useState('')
  const [bookingNumberError, setBookingNumberError] = useState(undefined)
  const [userLastname, setUserLastname] = useState('')
  const [userLastnamError, setUserLastnameError] = useState(undefined)

  const [locations, setLocations] = useState()

  useEffect(() => {
    axios.get('/cms/locations').then(({ data }) => {
      setLocations(data)
    })
  }, [])

  function useOutsideAlerter(ref) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          formStatus(null)
        }
      }
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }
  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef)

  const submitForm = () => {
    let error = false
    setFormStatus(null)
    setBookingNumberError(undefined)
    setUserLastnameError(undefined)

    if (isEmpty(bookingNumber)) {
      setBookingNumberError(['Please enter your booking number'])
      gtm.formErrorDisplay('cancel_booking', 'Please enter your booking number')
      error = true
    }
    if (isEmpty(userLastname)) {
      setUserLastnameError(['Please enter your last name'])
      gtm.formErrorDisplay('cancel_booking', 'Please enter your last name')
      error = true
    }

    if (!error) {
      setFormSubmitting.true()
      axios
        .post('/api', {
          method: 'RetrieveReservation',
          data: {
            bookingNumber,
            userLastname,
            index: 'default',
            cdp: '',
          },
          type: 'retrieveReservation',
        })
        .then(({ data }) => {
          setFormSubmitting.false()
          console.log('DATA', data)
          if (data.result.api.hasOwnProperty('Errors')) {
            gtm.formErrorDisplay(
              'cancel_booking',
              "We couldn't find your reservation for the details below.",
            )
            setFormStatus(false)
          } else {
            setBookingDetails(data.result.api.VehRetResRSCore.VehReservation)
            setFormStatus(true)
          }
        })
        .catch(err => {
          console.error(err)
        })
    }
  }

  const cancelBooking = () => {
    if (confirm('Are you sure you want to cancel your booking?')) {
      setFormSubmitting.true()
      axios
        .post('/api', {
          method: 'CancelBooking',
          data: {
            bookingNumber,
            userLastname,
            index: 'default',
            cdp: '',
          },
          type: 'CancelBooking',
        })
        .then(({ data }) => {
          setFormSubmitting.false()
          if (data.result.api.hasOwnProperty('Success')) {
            gtm.cancelBooking(bookingDetails)
            top.location = '/booking-cancelled'
          } else {
            gtm.formErrorDisplay('cancel_booking', 'Something went wrong. Please try again later')
            alert('Something went wrong. Please try again later')
          }
          console.log('CancelBooking', data)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.innerCont}>
        <div className={classes.formsec}>
          <div className={classes.formarea}>
            {cancelled ? (
              <div>
                <div className={classes.buttons}>
                  <ButtonCta primary text="Cancel another booking" url="/cancel-booking" />
                  <ButtonCta secondary text="Make another booking" url="/" />
                </div>
              </div>
            ) : (
              <div className={classes.form}>
                <h3>Please enter your booking details below.</h3>
                {formStatus === false && (
                  <div style={{ color: '#EE2049', fontSize: 14, marginBottom: 7 }}>
                    We couldn&apos;t find your reservation for the details below.
                  </div>
                )}
                <div className={classes.flexContFull}>
                  <FormInput
                    greyBackground={true}
                    disabled={formSubmitting}
                    error={bookingNumberError}
                    labelText="Booking Number*"
                    inputType="text"
                    defaultValue=""
                    onChange={el => {
                      setBookingNumber(el.currentTarget.value)
                    }}
                  />
                </div>
                <div className={classes.flexContFull}>
                  <FormInput
                    greyBackground={true}
                    disabled={formSubmitting}
                    error={userLastnamError}
                    labelText="Last name*"
                    inputType="text"
                    defaultValue=""
                    onChange={el => {
                      setUserLastname(el.currentTarget.value)
                    }}
                  />
                </div>
                <div className={classes.flexContFull}>
                  <div style={{ paddingTop: 20 }}>
                    <button
                      disabled={formSubmitting}
                      onClick={submitForm}
                      className={classes.buttonPrimary}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {formStatus === true && bookingDetails && (
        <div
          style={{
            backgroundColor: 'rgba(0,0,0,0.4)',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 99999,
            padding: '15px',
            boxSizing: 'border-box',
          }}
        >
          <div
            ref={wrapperRef}
            style={{
              width: '100%',
              maxWidth: '650px',
              margin: '5vh auto 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '24px 35px',
                backgroundColor: '#007AC3',
                borderRadius: '30px 30px 0px 0px',
              }}
            >
              <div
                style={{
                  maxWidth: '300px',
                  fontFamily: 'TTRounds',
                  fontStyle: 'normal',
                  fontWeight: 'bold',
                  fontSize: '26px',
                  lineHeight: '30px',
                  color: '#fff',
                }}
              >
                Your booking details
              </div>
              <div style={{ paddingLeft: 10 }}>
                <button
                  type="button"
                  style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}
                  onClick={() => setFormStatus(null)}
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M18.3525 1.38372C17.7667 0.797933 16.8169 0.797933 16.2311 1.38372L9.86719 7.74768L3.50323 1.38372C2.91744 0.797932 1.96769 0.797932 1.38191 1.38372C0.796119 1.96951 0.796119 2.91925 1.38191 3.50504L7.74587 9.869L1.3819 16.233C0.796119 16.8187 0.796118 17.7685 1.3819 18.3543C1.96769 18.9401 2.91744 18.9401 3.50323 18.3543L9.86719 11.9903L16.2311 18.3543C16.8169 18.9401 17.7667 18.9401 18.3525 18.3543C18.9383 17.7685 18.9383 16.8187 18.3525 16.233L11.9885 9.869L18.3525 3.50504C18.9383 2.91925 18.9383 1.96951 18.3525 1.38372Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div
              style={{
                padding: '24px 35px',
                backgroundColor: '#fff',
                borderRadius: ' 0px 0px 30px 30px',
              }}
            >
              <div
                style={{
                  fontFamily: 'TTRounds',
                  fontStyle: 'normal',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  lineHeight: '22px',
                  color: '#007AC3',
                  marginBottom: 10,
                }}
              >
                Please verify your booking details below.
              </div>
              <div
                style={{
                  fontStyle: 'normal',
                  fontWeight: 300,
                  fontSize: '16px',
                  lineHeight: '22px',
                }}
              >
                <div
                  style={{
                    marginBottom: 30,
                  }}
                >
                  Please see your booking details below before you cancel your booking.
                </div>

                <div style={{ marginBottom: 25 }}>
                  <div>
                    <div style={{ marginBottom: 25 }}>
                      <div className={classes.popupTitle}>Booking details</div>
                      <div className={classes.popupItem}>
                        <div>Booking ID:</div>
                        <div>{bookingDetails.VehSegmentCore.ConfID['@attributes'].ID}</div>
                      </div>
                      <div className={classes.popupItem}>
                        <div>Full name:</div>
                        <div>{bookingDetails.Customer.Primary.PersonName.Surname}</div>
                      </div>
                      {/* <div className={classes.popupItem}>
                        <div>Total Charge:</div>
                        <div>
                          <sup style={{ fontSize: 12, fontWeight: 'bold', paddingRight: 2 }}>$</sup>
                          {
                            bookingDetails.VehSegmentCore.TotalCharge['@attributes']
                              .EstimatedTotalAmount
                          }
                          <sup style={{ fontSize: 12, fontWeight: 'bold', paddingLeft: 2 }}>
                            {bookingDetails.VehSegmentCore.TotalCharge['@attributes'].CurrencyCode}
                          </sup>
                        </div>
                      </div> */}
                    </div>

                    {bookingDetails.VehSegmentCore.VehRentalCore.hasOwnProperty(
                      'PickUpLocation',
                    ) && (
                      <>
                        <div style={{ marginBottom: 25 }}>
                          <div className={classes.popupTitle}>Pick Up Details</div>
                          <div className={classes.popupItem}>
                            <div>Pick Up Location:</div>
                            <div>
                              {locations.find(
                                location =>
                                  location.code ===
                                  bookingDetails.VehSegmentCore.VehRentalCore.PickUpLocation[
                                    '@attributes'
                                  ].LocationCode,
                              )?.name ||
                                bookingDetails.VehSegmentCore.VehRentalCore.PickUpLocation[
                                  '@attributes'
                                ].LocationCode ||
                                ''}
                            </div>
                          </div>
                          <div className={classes.popupItem}>
                            <div>Pick Up Time:</div>
                            <div>
                              {moment
                                .parseZone(
                                  bookingDetails.VehSegmentCore.VehRentalCore['@attributes']
                                    .PickUpDateTime,
                                )
                                .utc(true)
                                .format('MMMM Do YYYY, h:mm a')}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {bookingDetails.VehSegmentCore.VehRentalCore.hasOwnProperty(
                      'ReturnLocation',
                    ) && (
                      <>
                        <div style={{ marginBottom: 25 }}>
                          <div className={classes.popupTitle}>Drop Off Details</div>
                          <div className={classes.popupItem}>
                            <div>Drop Off Location:</div>
                            <div>
                              {locations.find(
                                location =>
                                  location.code ===
                                  bookingDetails.VehSegmentCore.VehRentalCore.ReturnLocation[
                                    '@attributes'
                                  ].LocationCode,
                              )?.name ||
                                bookingDetails.VehSegmentCore.VehRentalCore.ReturnLocation[
                                  '@attributes'
                                ].LocationCode ||
                                ''}
                            </div>
                          </div>
                          <div className={classes.popupItem}>
                            <div>Drop Off Time:</div>
                            <div>
                              {moment
                                .parseZone(
                                  bookingDetails.VehSegmentCore.VehRentalCore['@attributes']
                                    .ReturnDateTime,
                                )
                                .utc(true)
                                .format('MMMM Do YYYY, h:mm a')}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    <div style={{ marginBottom: 25 }}>
                      <div className={classes.popupTitle}>Vehicle Details</div>
                      <div className={classes.popupItem}>
                        <div>Vehicle:</div>
                        <div>
                          {bookingDetails.VehSegmentCore.Vehicle.VehMakeModel['@attributes'].Name}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <button
                      disabled={formSubmitting}
                      onClick={cancelBooking}
                      className={classes.cancelButton}
                      type="button"
                    >
                      Cancel Booking
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CancelBookingForm
